interface NavigateQueryDetails<D = any> {
  readonly query: string;
  readonly type: 'boolean' | 'number' | 'string' | 'json';
  readonly isArray?: boolean;
  readonly default?: D;
  /** Automatically re-applied on every navigation and hard redirect. */
  readonly sticky?: boolean;
}
type NavigateQueriesDetails<T extends string, D = any> = {[key in T]: D}

const hasQuery = (url: string, key: string): boolean => {
  const u = new URL(url);
  const result = u.searchParams.has(key);
  return result;
}

const getQueryAsString = (url: string, key: string, defaultValue?: string): string | undefined => {
  const u = new URL(url);
  const result = u.searchParams.get(key);
  if (!result) return defaultValue;
  return result;
}

const getQueryAsBoolean = (url: string, key: string, defaultValue?: boolean): boolean | undefined => {
  const result = getQueryAsString(url, key, undefined);
  if (result === undefined) {
    if (hasQuery(url, key)) return true;
    return defaultValue;
  }
  return result === 'true' || result === '1';
}

const getQueryAsNumber = (url: string, key: string, defaultValue: number): number => {
  const result = getQueryAsString(url, key, undefined);
  let num : number = Number(result);
  if (Number.isNaN(num)) return defaultValue;
  return num;
}

const getQueryAsJsonObject = <T = any>(url: string, key: string, defaultValue?: T, isArray?: boolean): T | undefined => {
  const result = getQueryAsString(url, key, undefined);
  if (!result) return defaultValue;
  let json: any | undefined;
  try {
    json = JSON.parse(result);
    if (isArray === true && !Array.isArray(json)) {
      json = defaultValue;
    }
  } catch(err) {
    json = defaultValue;
  }
  return json;
}

const getQueryAsStringArray = (url: string, key: string, defaultValue?: string[]): string[] | undefined => {
  const result = getQueryAsJsonObject(url, key, undefined, true);
  if (result === undefined) return defaultValue;
  const array: string[] = [];
  array.forEach((value: any) => {
    if (typeof value === 'string') array.push(value);
  });
  return array;
}

const getQueryAsBooleanArray = (url: string, key: string, defaultValue?: boolean[]): boolean[] | undefined => {
  const result = getQueryAsJsonObject(url, key, undefined, true);
  if (result === undefined) return defaultValue;
  const array: boolean[] = [];
  array.forEach((value: any) => {
    if (typeof value === 'boolean') array.push(value);
  });
  return array;
}

const getQueryAsNumberArray = (url: string, key: string, defaultValue: number[]): number[] => {
  const result = getQueryAsJsonObject(url, key, undefined, true);
  if (result === undefined) return defaultValue;
  const array: number[] = [];
  array.forEach((value: any) => {
    if (typeof value === 'number') array.push(value);
  });
  return array;
}

export const getQuery = <T extends string, M extends any>(url: string, key: T, details: NavigateQueriesDetails<T, NavigateQueryDetails<M>>) : M | undefined => {
  const queryDetails = details[key];
  if (queryDetails.type === 'string') {
    if (queryDetails.isArray) return getQueryAsStringArray(url, queryDetails.query, queryDetails.default as any) as any;
    return getQueryAsString(url, queryDetails.query, queryDetails.default as any) as any;
  }
  if (queryDetails.type === 'boolean') {
    if (queryDetails.isArray) return getQueryAsBooleanArray(url, queryDetails.query, queryDetails.default as any) as any;
    return getQueryAsBoolean(url, queryDetails.query, queryDetails.default as any) as any;
  }
  if (queryDetails.type === 'number') {
    if (queryDetails.isArray) return getQueryAsNumberArray(url, queryDetails.query, queryDetails.default as any) as any;
    return getQueryAsNumber(url, queryDetails.query, queryDetails.default as any) as any;
  }
  if (queryDetails.type === 'json') {
    return getQueryAsJsonObject(url, queryDetails.query, queryDetails.default as any) as any;
  }
}

export const getQueryLabel =  <T extends string, M extends any>(key: T, details: NavigateQueriesDetails<T, NavigateQueryDetails<M>>) : string => {
  return details[key].query;
}

// ---------------------- GLOBAL -------------------- //
// Single catalog of every query the app reads from the URL. `sticky: true`
// entries are re-applied on every navigation; the others are page-scoped and
// only carried when explicitly named in `forwardParams`.
export enum NavigateGlobalQuery {
  SLAVE = 'SLAVE',

  SCREEN_INDEX = 'SCREEN_INDEX',
  SCREEN_NAME = 'SCREEN_NAME',
  SCREEN_LAYOUT_ID = 'SCREEN_LAYOUT_ID',

  DISABLE_GOOGLE_LOGIN = 'DISABLE_GOOGLE_LOGIN',
  SETTINGS = 'SETTINGS',
  TOKEN = "TOKEN",
  REFRESH_TOKEN = "REFRESH_TOKEN",

  PROVIDER = 'PROVIDER',
  MEETING = 'MEETING',
  AUTOJOIN = 'AUTOJOIN',
  VOD_ID = 'VOD_ID',
  LIST_TAB = 'LIST_TAB',
  REDIRECT = 'REDIRECT',
  FORBIDDEN_REASON = 'FORBIDDEN_REASON',
  BACKEND_ERROR = 'BACKEND_ERROR',
}

export interface NavigateGlobalQueryTypeMap {
  [NavigateGlobalQuery.SLAVE]: boolean,
  [NavigateGlobalQuery.SCREEN_INDEX]: number,
  [NavigateGlobalQuery.SCREEN_NAME]: string,
  [NavigateGlobalQuery.SCREEN_LAYOUT_ID]: number,
  [NavigateGlobalQuery.DISABLE_GOOGLE_LOGIN]: boolean,
  [NavigateGlobalQuery.SETTINGS]: string,
  [NavigateGlobalQuery.TOKEN]: string,
  [NavigateGlobalQuery.REFRESH_TOKEN]: string,
  [NavigateGlobalQuery.PROVIDER]: string,
  [NavigateGlobalQuery.MEETING]: string,
  [NavigateGlobalQuery.AUTOJOIN]: boolean,
  [NavigateGlobalQuery.VOD_ID]: string,
  [NavigateGlobalQuery.LIST_TAB]: string,
  [NavigateGlobalQuery.REDIRECT]: string,
  [NavigateGlobalQuery.FORBIDDEN_REASON]: string,
  [NavigateGlobalQuery.BACKEND_ERROR]: string,
}

interface NavigateGlobalQueryDetails<T extends NavigateGlobalQuery> extends NavigateQueryDetails<NavigateGlobalQueryTypeMap[T]> {}

const NAVIGATE_GLOBAL_QUERIES: NavigateQueriesDetails<NavigateGlobalQuery, NavigateGlobalQueryDetails<NavigateGlobalQuery>> = {
  [NavigateGlobalQuery.SLAVE]: {
    query: 'slave',
    type: 'boolean',
    sticky: true,
  },
  [NavigateGlobalQuery.SCREEN_INDEX]: {
    query: 'screen',
    type: 'number',
    sticky: true,
  },
  [NavigateGlobalQuery.SCREEN_NAME]: {
    query: 'screen_name',
    type: 'string',
    sticky: true,
  },
  [NavigateGlobalQuery.SCREEN_LAYOUT_ID]: {
    query: 'screen_layout_id',
    type: 'number',
    sticky: true,
  },
  [NavigateGlobalQuery.DISABLE_GOOGLE_LOGIN]: {
    query: 'disable_google_login',
    type: 'boolean',
    sticky: true,
  },
  [NavigateGlobalQuery.SETTINGS]: {
    query: 'settings',
    type: 'string',
    sticky: true,
  },
  [NavigateGlobalQuery.TOKEN]: {
    query: 'token',
    type: 'string',
  },
  [NavigateGlobalQuery.REFRESH_TOKEN]: {
    query: 'refreshToken',
    type: 'string',
  },
  [NavigateGlobalQuery.PROVIDER]: {
    query: 'provider',
    type: 'string',
  },
  [NavigateGlobalQuery.MEETING]: {
    query: 'meeting',
    type: 'string',
  },
  [NavigateGlobalQuery.AUTOJOIN]: {
    query: 'autojoin',
    type: 'boolean',
    sticky: true,
  },
  [NavigateGlobalQuery.VOD_ID]: {
    query: 'vodId',
    type: 'string',
  },
  [NavigateGlobalQuery.LIST_TAB]: {
    query: 'tab',
    type: 'string',
  },
  [NavigateGlobalQuery.REDIRECT]: {
    query: 'redirect',
    type: 'string',
  },
  [NavigateGlobalQuery.FORBIDDEN_REASON]: {
    query: 'r',
    type: 'string',
  },
  [NavigateGlobalQuery.BACKEND_ERROR]: {
    query: 'backend_error',
    type: 'string',
  },
} as const;

export const getGlobalQuery = <T extends NavigateGlobalQuery>(url: string, key: T): NavigateGlobalQueryTypeMap[T] | undefined => {
  return getQuery(url, key, NAVIGATE_GLOBAL_QUERIES) as NavigateGlobalQueryTypeMap[T];
}

export const getGlobalQueryLabel =  <T extends NavigateGlobalQuery>(key: T) : string => {
  return getQueryLabel(key, NAVIGATE_GLOBAL_QUERIES);
}

// ---------------------- MASTER -------------------- //

/*
export enum NavigateMasterQuery {
}

export interface NavigateMasterQueryTypeMap {
}

interface NavigateMasterQueryDetails<T extends NavigateMasterQuery> extends NavigateQueryDetails<NavigateMasterQueryTypeMap[T]> {}

const NAVIGATE_MASTER_QUERIES: NavigateQueriesDetails<NavigateMasterQuery, NavigateMasterQueryDetails<NavigateMasterQuery>> = {
} as const;

export const getMasterQuery = <T extends NavigateMasterQuery>(url: string, key: T): NavigateMasterQueryTypeMap[T] | undefined => {
  return getQuery(url, key, NAVIGATE_MASTER_QUERIES);
}

export const getMasterQueryLabel =  <T extends NavigateMasterQuery>(key: T) : string => {
  return getQueryLabel(key, NAVIGATE_MASTER_QUERIES);
}
*/

// ---------------------- SLAVE -------------------- //
export enum NavigateSlaveQuery {
  BRIDGE_WEBSOCKET_ENDPOINT = 'BRIDGE_WEBSOCKET_ENDPOINT',
}

export interface NavigateSlaveQueryTypeMap {
  [NavigateSlaveQuery.BRIDGE_WEBSOCKET_ENDPOINT]: string,
}

interface NavigateSlaveQueryDetails<T extends NavigateSlaveQuery> extends NavigateQueryDetails<NavigateSlaveQueryTypeMap[T]> {}

const NAVIGATE_SLAVE_QUERIES: NavigateQueriesDetails<NavigateSlaveQuery, NavigateSlaveQueryDetails<NavigateSlaveQuery>> = {
  [NavigateSlaveQuery.BRIDGE_WEBSOCKET_ENDPOINT]: {
    query: 'bridgeWebsocketEndpoint',
    type: 'string',
    sticky: true,
  },
} as const;

export const getSlaveQuery = <T extends NavigateSlaveQuery>(url: string, key: T): NavigateSlaveQueryTypeMap[T] | undefined => {
  return getQuery(url, key, NAVIGATE_SLAVE_QUERIES) as any;
}

export const getSlaveQueryLabel =  <T extends NavigateSlaveQuery>(key: T) : string => {
  return getQueryLabel(key, NAVIGATE_SLAVE_QUERIES);
}

// ---------------------- STICKY QUERIES -------------------- //

const STICKY_QUERY_LABELS: string[] = [
  ...Object.values(NAVIGATE_GLOBAL_QUERIES),
  ...Object.values(NAVIGATE_SLAVE_QUERIES),
].filter((details) => details.sticky).map((details) => details.query);

/**
 * Extract the sticky queries present in a search string (empty value =
 * absent), plus the catalogued queries named in `forwardParams`.
 */
export const getStickySearchParams = (currentSearch: string | URLSearchParams, forwardParams?: NavigateGlobalQuery[]): URLSearchParams => {
  const source = typeof currentSearch === 'string' ? new URLSearchParams(currentSearch) : currentSearch;
  const result = new URLSearchParams();
  const forwardedLabels = (forwardParams ?? []).map((key) => NAVIGATE_GLOBAL_QUERIES[key].query);
  [...STICKY_QUERY_LABELS, ...forwardedLabels].forEach((label) => {
    const value = source.get(label);
    if (value !== null && value !== '') result.set(label, value);
  });
  return result;
}

/**
 * Re-apply the sticky queries of `currentSearch` onto a target path or URL,
 * plus the catalogued queries named in `forwardParams`. Queries already
 * present on the target win over carried values.
 */
export const applyStickyParams = (target: string, currentSearch: string | URLSearchParams, forwardParams?: NavigateGlobalQuery[]): string => {
  const queryIndex = target.indexOf('?');
  const base = queryIndex === -1 ? target : target.slice(0, queryIndex);
  const params = new URLSearchParams(queryIndex === -1 ? '' : target.slice(queryIndex + 1));
  getStickySearchParams(currentSearch, forwardParams).forEach((value, key) => {
    if (!params.has(key)) params.set(key, value);
  });
  const search = params.toString();
  return search ? `${base}?${search}` : base;
}
